import React from 'react';
import { View, ViewProps } from 'react-native';

type FlashListPlainViewCellProps = ViewProps & {
  /** FlashList passes the row index for its own native cell; a View ignores it. */
  index?: number;
};

/**
 * TA-11104: `CellRendererComponent` for a FlashList whose rows end up at the
 * wrong position on iOS (a blank list, or rows drawn on top of each other,
 * after a fast scroll back up, a far jump, or a data change while scrolled).
 *
 * Cause: FlashList 1.x's native cell view is not registered as a Fabric
 * component in this app, so React Native mounts it through its legacy interop
 * layer, and that layer drops the position JS gives each cell. FlashList's
 * native layout pass then stacks the cells under one another, which goes wrong
 * as soon as a cell is reused out of order. A plain View is positioned by
 * Fabric directly, so rows render where JS puts them.
 *
 * Always pair it with `disableAutoLayout`: that native pass only understands
 * FlashList's own cell. The cost is that a row whose size is only an estimate
 * is painted at the estimated position first and corrected a moment later, so
 * give the list exact sizes (`overrideItemLayout`) where possible.
 *
 * `collapsable={false}` keeps one real native view per row. Without it React
 * Native flattens this wrapper and hoists each row's children into the list.
 *
 * Temporary: remove once FlashList's native views are registered for Fabric,
 * or FlashList is upgraded to a version that is.
 */
export const FlashListPlainViewCell = React.forwardRef<
  View,
  FlashListPlainViewCellProps
>(({ index: _index, ...props }, ref) => (
  <View ref={ref} collapsable={false} {...props} />
));
FlashListPlainViewCell.displayName = 'FlashListPlainViewCell';
